// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * When a node's status dot pulses, so it blinks as each flow dash arrives.
 *
 * The flow overlay is a dash pattern (`FLOW_DASH` on, `FLOW_PERIOD` per
 * repeat) whose offset runs down the conductor once per `FLOW_DURATION_MS`.
 * Every flow and pulse animation is locked to the document timeline
 * (`lockFlowPhase`), so a dash's leading edge reaches a conductor's far end at
 * a fixed phase set by the conductor's length, and the dot at that end is
 * given the same phase as its animation delay. The result: the dot flashes as
 * the dash touches it, which is what says "this is the power feeding me".
 *
 * These three numbers must match `.sld-edge-flow` and `@keyframes sld-flow` in
 * `PowerFlowPage.css`; the test pins them.
 */

import type { ConductorAnchor, PowerFlowConductor } from '../../../../utils/powerFlowRouting';

export const FLOW_DASH = 6;
export const FLOW_PERIOD = 16;
export const FLOW_DURATION_MS = 1200;

/** A node's feed: pulsing in step with the dash that arrives, or not fed. */
export type NodeFeed = { delayMs: number } | 'quiet';

/** Length of a conductor's polyline. */
export const polylineLength = (points: readonly ConductorAnchor[]): number => {
  let length = 0;
  for (let index = 1; index < points.length; index += 1) {
    length += Math.hypot(
      points[index].x - points[index - 1].x,
      points[index].y - points[index - 1].y
    );
  }
  return length;
};

const mod = (value: number, by: number) => ((value % by) + by) % by;

/**
 * Delay that gives a separate flow path, drawn from `at` px down a conductor,
 * the same dash phase the conductor has there — the breaker arc rides the run
 * without its dash jumping at the cut. Meaningful only under `lockFlowPhase`.
 */
export const dashDelayAt = (at: number): number =>
  (mod(at, FLOW_PERIOD) / FLOW_PERIOD) * FLOW_DURATION_MS;

/** Delay that puts a pulse's peak where a dash's leading edge is `at` px down the path. */
const delayAt = (at: number): number => dashDelayAt(at - FLOW_DASH);

/**
 * Every drawn node's feed.
 *
 * A node fed by a conductor carrying load pulses as that conductor's dash
 * arrives at it. A source with no such input but a flowing output pulses as
 * the dash leaves it. Everything else is quiet.
 *
 * ponytail: a node fed by two flowing conductors takes the first by id; phase
 * both only if a dual-fed dot flashing twice is ever wanted.
 */
export const nodeFeeds = (
  conductors: readonly PowerFlowConductor[],
  isFlowing: (_conductor: PowerFlowConductor) => boolean
): Map<string, NodeFeed> => {
  const feeds = new Map<string, NodeFeed>();
  const flowing = conductors.filter(isFlowing).sort((a, b) => (a.id < b.id ? -1 : 1));
  for (const conductor of flowing) {
    if (feeds.has(conductor.target)) continue;
    feeds.set(conductor.target, { delayMs: delayAt(polylineLength(conductor.points)) });
  }
  for (const conductor of flowing) {
    if (!feeds.has(conductor.source)) feeds.set(conductor.source, { delayMs: delayAt(0) });
  }
  return feeds;
};

/** CSS animations the phase lock aligns. */
const PHASED_ANIMATIONS = new Set(['sld-flow', 'sld-dot-pulse']);

/**
 * Align every flow dash and status pulse under `root` to the document
 * timeline, so their phases depend only on their delays and never on when each
 * element happened to mount. A no-op where the Web Animations API is missing.
 */
export const lockFlowPhase = (root: Element | null): void => {
  const animations = root?.getAnimations?.({ subtree: true }) ?? [];
  for (const animation of animations) {
    const name = (animation as Animation & { animationName?: string }).animationName;
    if (name && PHASED_ANIMATIONS.has(name) && animation.startTime !== 0) {
      animation.startTime = 0;
    }
  }
};
